import React from 'react';
import type { Test, User } from '../types';
import { LAPSE_THRESHOLD_MS, MAJOR_LAPSE_THRESHOLD_MS } from '../config/testMetrics';
import './PrintableResults.css';

interface TestResultsDisplayProps {
  test: Test & { user?: User };
  showUserInfo?: boolean;
  showTestInfo?: boolean;
  className?: string;
}

const TestResultsDisplay: React.FC<TestResultsDisplayProps> = ({
  test,
  showUserInfo = true,
  showTestInfo = true,
  className = ''
}) => {
  const { results } = test;

  if (!results) {
    return (
      <div className={`bg-navy-800 shadow rounded-lg p-6 ${className}`}>
        <div className="text-center text-navy-300">
          <p>No results available for this test.</p>
        </div>
      </div>
    );
  }

  const falseStartRate = results.totalResponses > 0 ? (results.totalFalseStarts / results.totalResponses * 100) : 0;
  const alertnessLevel = results.alertnessLevel ?? 'normal';
  const alertnessLabel = alertnessLevel === 'reduced' ? 'Reduced' : 'Normal';
  const alertnessColor = alertnessLevel === 'reduced' ? 'text-red-400' : 'text-green-400';
  const employeeIdentifier = test.employee?.employeeId || test.employeeId;
  const formatDuration = (durationSeconds?: number) => {
    if (!durationSeconds || durationSeconds <= 0) return 'N/A';
    const totalSeconds = Math.round(durationSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`printable space-y-6 ${className}`}>
      {/* Header with Performance Indicator */}
      <div className="bg-navy-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="hidden print:block text-sm font-semibold text-black">Vigikus Fatigue Check</p>
            <h3 className="text-lg font-medium text-white">Fatigue Check results</h3>
            {showTestInfo && (
              <p className="text-sm text-navy-300">
                Completed on {new Date(test.testDate).toLocaleDateString()} at{' '}
                {new Date(test.testDate).toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 text-right">
            <p className={`text-sm font-medium ${alertnessColor}`}>Alertness: {alertnessLabel}</p>
            <button type="button" onClick={() => window.print()} className="print:hidden text-sm font-medium text-gold-400 underline hover:text-gold-300">
              Print results
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Average Reaction Time */}
        <div className="bg-navy-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-xs font-medium">AVG</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-navy-300">
                    Average Reaction Time
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {Math.round(results.averageReactionTime)} ms
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Responses */}
        <div className="bg-navy-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-navy-700 rounded-md flex items-center justify-center">
                  <span className="text-gold-400 text-xs font-medium">N</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-navy-300">
                    Responses
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {results.totalResponses}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* False Starts */}
        <div className="bg-navy-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-md flex items-center justify-center">
                  <span className="text-orange-300 text-xs font-medium">FS</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-navy-300">
                    False Starts
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {results.totalFalseStarts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Lapses */}
        <div className="bg-navy-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-xs font-medium">LAP</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-navy-300">
                    Total Lapses
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {results.totalLapses}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Performance Metrics */}
      <div className="bg-navy-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-white mb-4">
            Detailed Performance Metrics
          </h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Response Statistics */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3">Response Statistics</h4>
                <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">False start rate:</dt>
                  <dd className="text-sm font-medium text-white">
                    {falseStartRate.toFixed(1)}%
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">
                    {test.testType === 'LETTER' ? 'Major lapses (>1,000 ms; includes missed targets):' : 'Major lapses (>1,000 ms):'}
                  </dt>
                  <dd className="text-sm font-medium text-white">
                    {results.majorLapses ?? 0}
                  </dd>
                </div>
                {typeof results.missedTargets === 'number' && results.missedTargets > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-navy-300">Missed targets:</dt>
                    <dd className="text-sm font-medium text-white">{results.missedTargets}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Minor lapses ({LAPSE_THRESHOLD_MS}–{MAJOR_LAPSE_THRESHOLD_MS.toLocaleString()} ms):</dt>
                  <dd className="text-sm font-medium text-white">
                    {results.minorLapses ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Lapse threshold:</dt>
                  <dd className="text-sm font-medium text-white">≥{LAPSE_THRESHOLD_MS} ms</dd>
                </div>
              </dl>
            </div>
            
            {/* Performance Indicators */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3">Performance Indicators</h4>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Median Reaction Time:</dt>
                  <dd className="text-sm font-medium text-white">
                    {Math.round(results.medianReactionTime ?? 0)} ms
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Fastest 10% Mean:</dt>
                  <dd className="text-sm font-medium text-white">
                    {Math.round(results.fastestTenMean ?? 0)} ms
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Slowest 10% Mean:</dt>
                  <dd className="text-sm font-medium text-white">
                    {Math.round(results.slowestTenMean ?? 0)} ms
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Consistency:</dt>
                  <dd className="text-sm font-medium text-white">
                    {Math.ceil(results.highestReactionTime) - Math.ceil(results.lowestReactionTime)} ms range
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-navy-300">Std Dev:</dt>
                  <dd className="text-sm font-medium text-white">
                    {Math.round(results.reactionTimeStdDev ?? 0)} ms
                  </dd>
                </div>
              </dl>
            </div>

            {/* Test Information */}
            {(showTestInfo || showUserInfo) && (
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Test Information</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-navy-300">Test Duration:</dt>
                    <dd className="text-sm font-medium text-white">
                      {formatDuration(results.testDuration)}
                    </dd>
                  </div>
                  {test.employee?.name && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Employee:</dt>
                      <dd className="text-sm font-medium text-white">{test.employee.name}</dd>
                    </div>
                  )}
                  {employeeIdentifier && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Employee ID:</dt>
                      <dd className="text-sm font-medium text-white">{employeeIdentifier}</dd>
                    </div>
                  )}
                  {test.department && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Department:</dt>
                      <dd className="text-sm font-medium text-white">{test.department}</dd>
                    </div>
                  )}
                  {test.workShift && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Work Shift:</dt>
                      <dd className="text-sm font-medium text-white">{test.workShift}</dd>
                    </div>
                  )}
                  {typeof test.lastSleepDuration === 'number' && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Last Sleep:</dt>
                      <dd className="text-sm font-medium text-white">{test.lastSleepDuration} hrs</dd>
                    </div>
                  )}
                  {typeof test.sleepQuality === 'number' && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Sleep Quality:</dt>
                      <dd className="text-sm font-medium text-white">{test.sleepQuality}</dd>
                    </div>
                  )}
                  {typeof test.sleepinessLevel === 'number' && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-navy-300">Sleepiness Level:</dt>
                      <dd className="text-sm font-medium text-white">{test.sleepinessLevel}</dd>
                    </div>
                  )}
                  {showTestInfo && (
                    <>
                      <div className="flex justify-between">
                        <dt className="text-sm text-navy-300">Date:</dt>
                        <dd className="text-sm font-medium text-white">
                          {new Date(test.testDate).toLocaleDateString()}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reaction Time Distribution */}
      <div className="bg-navy-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-white mb-4">
            Reaction Time Distribution
          </h3>
          
          <div className="space-y-4">
            {/* Simple histogram representation */}
            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              <div className="text-center">
                <div className="text-navy-300 mb-1">&lt;{LAPSE_THRESHOLD_MS} ms</div>
                <div className="bg-green-500/20 h-8 flex items-end justify-center">
                  <span className="text-green-400 font-medium">
                    {results.rawReactionTimes.filter(time => time < LAPSE_THRESHOLD_MS).length}
                  </span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 mb-1">{LAPSE_THRESHOLD_MS}–500 ms</div>
                <div className="bg-navy-600 h-8 flex items-end justify-center">
                  <span className="text-gold-300 font-medium">
                    {results.rawReactionTimes.filter(time => time >= LAPSE_THRESHOLD_MS && time <= 500).length}
                  </span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 mb-1">&gt;500–1,000 ms</div>
                <div className="bg-yellow-500/20 h-8 flex items-end justify-center">
                  <span className="text-yellow-400 font-medium">
                    {results.rawReactionTimes.filter(time => time > 500 && time <= MAJOR_LAPSE_THRESHOLD_MS).length}
                  </span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 mb-1">&gt;{MAJOR_LAPSE_THRESHOLD_MS.toLocaleString()} ms</div>
                <div className="bg-red-500/20 h-8 flex items-end justify-center">
                  <span className="text-red-400 font-medium">
                    {results.rawReactionTimes.filter(time => time > MAJOR_LAPSE_THRESHOLD_MS).length}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="text-sm text-navy-200 text-center">
              Distribution of {results.rawReactionTimes.length} reaction time measurements
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResultsDisplay;
